import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '@app/services/api.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface Notification {
  id?: string;
  title: string;
  message: string;
  timestamp?: string;
  date?: string;
  status?: 'read' | 'unread';
  type?: string;
  label?: string;
  isRead?: boolean;
}

@Component({
  selector: 'app-notification-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-popup.component.html',
  styleUrls: ['./notification-popup.component.scss']
})
export class NotificationPopupComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() action = new EventEmitter<string>();

  activeTab: 'ACTIVE' | 'ARCHIVED' | 'ALL' = 'ACTIVE';
  notifications: Notification[] = [];
  unreadCount: number = 0;
  totalPages: number = 0;
  totalElements: number = 0;
  currentPage: number = 0;
  pageLimit: number = 5;
  loading: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadNotifications(): void {
    this.loading = true;
    this.apiService.getNotifications(this.currentPage, this.pageLimit, this.activeTab)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (response) {
            this.notifications = response.notifications || [];
            this.totalPages = response.totalPages || 0;
            this.totalElements = response.totalElements || 0;
            this.notifications = this.notifications.map((notif: any) => ({
              id: notif.id,
              title: notif.title || notif.subject || 'Notification',
              message: notif.message || notif.body || notif.description || '',
              timestamp: notif.timestamp || notif.createdAt || notif.date,
              date: notif.date || notif.timestamp || notif.createdAt,
              status: notif.status || (notif.isRead === false ? 'unread' : 'read'),
              isRead: notif.isRead !== undefined ? notif.isRead : (notif.status === 'read'),
              type: notif.type || notif.category,
              label: notif.label || notif.tag
            }));
            
            this.calculateUnreadCount();
          }
        },
        error: (error) => {
          this.loading = false;
          this.notifications = [];
        }
      });
  }

  calculateUnreadCount(): void {
    this.unreadCount = this.notifications.filter(n => !n.isRead).length;
  }

  setActiveTab(tab: 'ACTIVE' | 'ARCHIVED' | 'ALL'): void {
    this.activeTab = tab;
    this.currentPage = 0;
    this.loadNotifications();
  }

  onClose(): void {
    this.close.emit();
  }

  markAllAsRead(): void {
    // TODO: Implement mark all as read API call
    this.notifications.forEach(n => {
      n.isRead = true;
      n.status = 'read';
    });
    this.calculateUnreadCount();
  }

  archiveRead(): void {
    // TODO: Implement archive read API call
    this.notifications = this.notifications.filter(n => !n.isRead && n.status !== 'read');
    this.calculateUnreadCount();
  }

  formatTimeAgo(timestamp: string | undefined): string {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);
      
      if (diffMonths > 0) {
        return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
      } else if (diffWeeks > 0) {
        return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
      } else if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMins > 0) {
        return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      } else {
        return 'Just now';
      }
    } catch (error) {
      return '';
    }
  }
} 